import { MESSAGES_URL } from './messages.js';
import type { MessagingDetail } from './auditLog.js';

/**
 * messaging#58 (2026-10-21, option b) - the Audit log shows what each Messaging message said,
 * under its entry. The text is read LIVE from Messaging (POST /api/audit/messages, with the
 * sign-in cookie) for the page on screen, one request per page, while an admin or manager looks.
 * It is shown, never stored: not in the EHR's database and not in the CSV export. Messaging
 * records that the text was viewed (one "message.text.read" entry, a count only).
 */
const TEXT_URL = `${MESSAGES_URL}api/audit/messages`;
/** Messaging's limit, and the Audit log's page size: one request per page. */
export const TEXT_BATCH = 100;

export interface MessageText {
  status: 'ok' | 'deleted' | 'missing' | 'denied' | 'unavailable';
  text?: string;
  file?: { name: string; kind: string };
  /** edited later (or this is an edit) */
  edited?: boolean;
  /** what an edited message says now */
  editedText?: string;
}
export interface MessageRef {
  roomId: string;
  eventId: string;
}

export const messageKey = (m: MessageRef) => `${m.roomId} ${m.eventId}`;

/** The entry's message, when it is about one (a room and a message id). */
export function messageRef(d: MessagingDetail | null | undefined): MessageRef | null {
  return d?.roomId && d.eventId ? { roomId: d.roomId, eventId: d.eventId } : null;
}

/** The messages on a page, each once, in page order. */
export function messageRefs(rows: { messaging?: MessagingDetail | null }[]): MessageRef[] {
  const seen = new Map<string, MessageRef>();
  for (const r of rows) {
    const m = messageRef(r.messaging);
    if (m && !seen.has(messageKey(m))) seen.set(messageKey(m), m);
  }
  return [...seen.values()];
}

/**
 * Each message's text by messageKey. 'denied' when Messaging says this person may not read
 * message text (403): the Audit log then shows nothing extra. null when it failed.
 */
export async function fetchMessageTexts(
  refs: MessageRef[],
  fetchImpl: typeof fetch = fetch,
): Promise<Map<string, MessageText> | 'denied' | null> {
  const out = new Map<string, MessageText>();
  try {
    for (let i = 0; i < refs.length; i += TEXT_BATCH) {
      const r = await fetchImpl(TEXT_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: refs.slice(i, i + TEXT_BATCH) }),
      });
      if (r.status === 403) return 'denied';
      if (!r.ok) return null;
      const j = (await r.json()) as { items?: unknown };
      if (!Array.isArray(j.items)) return null;
      for (const it of j.items as (MessageText & Partial<MessageRef>)[]) {
        if (typeof it?.roomId !== 'string' || typeof it.eventId !== 'string') continue;
        out.set(messageKey({ roomId: it.roomId, eventId: it.eventId }), it);
      }
    }
    return out;
  } catch {
    return null;
  }
}

export interface MessageTextView {
  body: string;
  /** a note about the message ("(message deleted)"), not its words */
  muted: boolean;
  edited: boolean;
}

const UNAVAILABLE: MessageTextView = {
  body: 'Message text unavailable',
  muted: true,
  edited: false,
};

/**
 * The line under an entry: the words, "📎 name" for a file, "(message deleted)", or a quiet
 * "Message text unavailable". An "Edited a message" entry shows what the message says now.
 * `t` null = the read failed.
 */
export function messageTextView(t: MessageText | null, action: string): MessageTextView {
  if (!t) return UNAVAILABLE;
  if (t.status === 'deleted') return { body: '(message deleted)', muted: true, edited: false };
  if (t.status !== 'ok') return UNAVAILABLE;
  const update = action === 'message.update';
  const words = ((update && t.editedText) || t.text || '').trim();
  const file = t.file?.name ? `📎 ${t.file.name}` : '';
  const body = file && words ? `${file} · ${words}` : file || words;
  if (!body) return UNAVAILABLE;
  // "(edited)" on the other entries; an "Edited a message" entry is the edit
  return { body, muted: false, edited: !!t.edited && !update };
}
